import React, { createContext, useContext, useState, useEffect, useCallback } from 'react'
import { User } from '../types'
import { auth, users as usersApi } from '../api'

interface AuthCtx {
  user: User | null
  permissions: string[]
  loading: boolean
  login: (u: string, p: string) => Promise<void>
  logout: () => void
  setUserFromToken: (u: User) => void
  can: (moduleKey: string) => boolean
}
const Ctx = createContext<AuthCtx>({} as AuthCtx)

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null)
  const [permissions, setPermissions] = useState<string[]>([])
  const [loading, setLoading] = useState(true)

  const loadPermissions = async () => {
    try {
      const r: any = await usersApi.myPermissions()
      setPermissions(r.granted || [])
    } catch {
      setPermissions([])
    }
  }

  useEffect(() => {
    const token = localStorage.getItem('token')
    if (!token) { setLoading(false); return }
    auth.me()
      .then(async (u: any) => {
        setUser(u)
        await loadPermissions()
      })
      .catch(() => localStorage.removeItem('token'))
      .finally(() => setLoading(false))
  }, [])

  const login = async (username: string, password: string) => {
    const res: any = await auth.login(username, password)
    localStorage.setItem('token', res.access_token)
    setUser(res.user)
    await loadPermissions()
  }

  const logout = () => {
    localStorage.removeItem('token')
    document.cookie = 'puc_token=; path=/; max-age=0'
    setUser(null)
    setPermissions([])
  }
  const setUserFromToken = useCallback(async (u: User) => {
    setUser(u)
    await loadPermissions()
  }, [])

  const can = (moduleKey: string) => {
    if (!user) return false
    if (user.role === 'super_admin') return true
    if (!moduleKey) return true
    // 一级模块如 dashboard，只要任一子模块授权即可见
    if (!moduleKey.includes('.')) {
      if (permissions.includes(moduleKey)) return true
      return permissions.some(p => p.startsWith(moduleKey + '.'))
    }
    return permissions.includes(moduleKey)
  }

  return <Ctx.Provider value={{ user, permissions, loading, login, logout, setUserFromToken, can }}>{children}</Ctx.Provider>
}

export const useAuth = () => useContext(Ctx)
